import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { PageContainer } from "@/components/ui/page-container";
import { MobilePageTitle } from "@/components/ui/mobile-page-title";
import { Wizard } from "@/components/ui/wizard";
import type { WizardStep, WizardStepProps } from "@/components/ui/wizard/types";
import { useUnsavedChanges } from "@/hooks/use-unsaved-changes";
import { ConfirmDialog } from "@/components/ui/dialog/confirm-dialog";
import { toast } from "@/components/ui/toast";
import { getApiErrorMessages } from "@/utils/api-error";
import {
  PlanDetailsStep,
  WorkoutStep,
  GroupClassStep,
  ConfirmationStep,
} from "@/components/create-workout-plan/steps";
import { useSubmitWorkoutPlan } from "@/components/create-workout-plan/use-submit-workout-plan";
import { useAthleteNameBreadcrumb } from "@/hooks/use-breadcrumb/use-breadcrumb-overrides";
import { PLAN_TYPE } from "@/constants/workout-plans";
import { PROTECTED_ROUTES } from "@/utils/navigation";
import {
  WorkoutPlanSchema,
  type WorkoutPlanFormData,
} from "@/schemas/workout-plan";

export const Route = createFileRoute(
  "/(protected)/athletes/$athleteId/workout-plans/new/",
)({
  component: RouteComponent,
  // Present only when reached from an existing workout block's detail view
  // (not, e.g., while that block is still being created/duplicated) — lets
  // Cancel/Save return to that block's detail instead of the generic tab.
  validateSearch: (search: Record<string, unknown>) => ({
    workoutBlockId:
      search.workoutBlockId !== undefined
        ? Number(search.workoutBlockId)
        : undefined,
  }),
});

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { athleteId } = Route.useParams();
  const { workoutBlockId } = Route.useSearch();

  useAthleteNameBreadcrumb(Number(athleteId));

  const form = useForm<WorkoutPlanFormData>({
    resolver: zodResolver(WorkoutPlanSchema()),
    mode: "onSubmit",
    defaultValues: {
      name: "",
      weeklyFrequency: 1,
      planType: PLAN_TYPE.Training,
      groupClass: [],
      workout: {
        warmup: [],
        strength_training: [],
        cooldown: [],
      },
      workoutStepValid: false,
      workoutForceValidate: false,
    },
  });

  const planType = useWatch({ control: form.control, name: "planType" });

  // Stable across re-renders of `steps` (e.g. on language change) so switching
  // steps doesn't unmount GroupClassStep and lose its local UI state.
  const GroupClassStepWithAthlete = useMemo(
    () =>
      function GroupClassStepWithAthlete(
        stepProps: WizardStepProps<WorkoutPlanFormData>,
      ) {
        return <GroupClassStep {...stepProps} athleteId={Number(athleteId)} />;
      },
    [athleteId],
  );

  const previousPlanTypeRef = useRef(planType);
  useEffect(() => {
    if (previousPlanTypeRef.current !== planType) {
      form.setValue(
        "groupClass",
        planType === PLAN_TYPE.GroupClass
          ? [
              {
                tempId: crypto.randomUUID(),
                discipline: null,
                observations: "",
              },
            ]
          : [],
      );
      previousPlanTypeRef.current = planType;
    }
  }, [planType, form]);

  const steps: WizardStep<WorkoutPlanFormData>[] = useMemo(
    () => [
      {
        id: "details",
        label: t("pages:athletes.workoutPlanWizard.steps.planDetails"),
        component: PlanDetailsStep,
        fields: ["name", "weeklyFrequency", "planType"],
      },
      {
        id: "type-step",
        label: t("pages:athletes.workoutPlanWizard.steps.typeDetails"),
        component:
          planType === PLAN_TYPE.GroupClass
            ? GroupClassStepWithAthlete
            : WorkoutStep,
        fields: planType === PLAN_TYPE.GroupClass ? ["groupClass"] : undefined,
        validityField:
          planType === PLAN_TYPE.GroupClass ? undefined : "workoutStepValid",
        onValidationAttempt:
          planType === PLAN_TYPE.GroupClass
            ? undefined
            : () => form.setValue("workoutForceValidate", true),
        disableStepScroll: planType !== PLAN_TYPE.GroupClass,
      },
      {
        id: "confirmation",
        label: t("pages:athletes.workoutPlanWizard.steps.confirmation"),
        component: ConfirmationStep,
      },
    ],
    [t, planType, form, GroupClassStepWithAthlete],
  );

  const navigateBack = useCallback(
    () =>
      workoutBlockId
        ? navigate({
            to: PROTECTED_ROUTES.ATHLETE_WORKOUT_BLOCK_DETAIL,
            params: { athleteId, workoutBlockId: String(workoutBlockId) },
          })
        : navigate({
            to: PROTECTED_ROUTES.ATHLETE_DETAIL,
            params: { athleteId },
            search: { tab: "workouts" },
          }),
    [navigate, athleteId, workoutBlockId],
  );

  const { handleCancelEdit, unsavedChangesDialogProps } = useUnsavedChanges({
    isEditing: form.formState.isDirty,
    isDirty: form.formState.isDirty,
    onLeave: navigateBack,
  });

  const numericWorkoutBlockId = Number(workoutBlockId);
  const hasValidWorkoutBlockId =
    Number.isInteger(numericWorkoutBlockId) && numericWorkoutBlockId > 0;

  // Saving requires attaching to an existing block, so a missing/non-numeric
  // workoutBlockId (e.g. a hand-edited URL) means Guardar could never succeed.
  useEffect(() => {
    if (!hasValidWorkoutBlockId) {
      navigateBack();
    }
  }, [hasValidWorkoutBlockId, navigateBack]);

  const { submit, isSubmitting } = useSubmitWorkoutPlan(
    Number(athleteId),
    numericWorkoutBlockId,
  );

  const handleComplete = useCallback(
    async (data: WorkoutPlanFormData) => {
      try {
        await submit(data);
        toast.success(
          t("pages:athletes.workoutPlanWizard.toaster.success.title"),
          {
            description: t(
              "pages:athletes.workoutPlanWizard.toaster.success.description",
            ),
          },
        );
        navigateBack();
      } catch (error) {
        toast.error(t("pages:athletes.workoutPlanWizard.toaster.error.title"), {
          description: getApiErrorMessages(error as Error),
        });
      }
    },
    [submit, t, navigateBack],
  );

  const handleCancel = useCallback(
    () => handleCancelEdit(navigateBack),
    [handleCancelEdit, navigateBack],
  );

  if (!hasValidWorkoutBlockId) {
    return null;
  }

  return (
    <div className="flex h-full flex-col px-6 py-5">
      <MobilePageTitle>
        {t("pages:athletes.workoutPlanWizard.title")}
      </MobilePageTitle>
      <PageContainer
        className="flex min-h-0 min-w-[309px] flex-1 flex-col px-2 py-0 md:m-0 md:px-0 lg:mb-0"
        internalScroll
      >
        <Wizard
          form={form}
          steps={steps}
          onComplete={handleComplete}
          onCancel={handleCancel}
          isSubmitting={isSubmitting}
        />
      </PageContainer>
      <ConfirmDialog {...unsavedChangesDialogProps} />
    </div>
  );
}
